/**
 * @file errors.ts
 * @description Разбор ошибок сервера «вида сайта» (phase19.16 Р1; контракт
 * §0, §2, §4): конфликт версии части (409), запрет публикации чужих правок
 * (403), блокирующие проверки (409), запрет публикации с незакрытыми
 * проверками (403, SB125) и тексты для человека.
 *
 * Тексты — реестром «код → фраза» (OCP): новый код ошибки — новая строка, а
 * не ветка в компоненте. Сервер уже пишет по-русски, поэтому реестр нужен
 * только там, где кабинету есть что сказать точнее (что делать дальше).
 */

import { SITE_LIMITS, type SiteViewPart } from "@chaiberi/shared";

import { ApiError } from "../api-client";
import type { PublishCheck, SiteBlockedPart, SiteViewUserRef } from "./types";

/** Конфликт версии части: кто и когда изменил черновик раньше. */
export interface SiteVersionConflict {
  part: SiteViewPart | null;
  version: number | null;
  editedBy: SiteViewUserRef | null;
  editedAt: string | null;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/** 409 `site_view_version_conflict` → сведения о том, кто правил. */
export function parseVersionConflict(error: unknown): SiteVersionConflict | null {
  if (!(error instanceof ApiError) || error.code !== "site_view_version_conflict") return null;
  const details = isObject(error.details) ? error.details : {};
  const editedBy = isObject(details.editedBy) && typeof details.editedBy.name === "string"
    ? { id: typeof details.editedBy.id === "string" ? details.editedBy.id : null, name: details.editedBy.name }
    : null;
  return {
    part: typeof details.part === "string" ? (details.part as SiteViewPart) : null,
    version: typeof details.version === "number" ? details.version : null,
    editedBy,
    editedAt: typeof details.editedAt === "string" ? details.editedAt : null,
  };
}

/** 403 `site_view_publish_forbidden` → части, которые ждут чужой публикации. */
export function parsePublishForbidden(error: unknown): SiteBlockedPart[] | null {
  if (!(error instanceof ApiError) || error.code !== "site_view_publish_forbidden") return null;
  const details = isObject(error.details) ? error.details : {};
  return Array.isArray(details.blocked) ? (details.blocked as SiteBlockedPart[]) : [];
}

/** 409 `site_view_publish_blocked` → блокирующие проверки. */
export function parsePublishBlocked(error: unknown): PublishCheck[] | null {
  if (!(error instanceof ApiError) || error.code !== "site_view_publish_blocked") return null;
  const details = isObject(error.details) ? error.details : {};
  return Array.isArray(details.checks) ? (details.checks as PublishCheck[]) : [];
}

/** Подпись, если сервер не прислал свой текст запрета (SB125 п. 2). */
export const ACKNOWLEDGE_FORBIDDEN_TEXT =
  "Опубликовать с незакрытыми проверками может владелец магазина или сотрудник с правом настроек магазина";

/** 403 `site_view_acknowledge_forbidden` (SB125) → текст сервера для подписи под кнопкой. */
export function parseAcknowledgeForbidden(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== "site_view_acknowledge_forbidden") return null;
  return error.message || `${ACKNOWLEDGE_FORBIDDEN_TEXT}.`;
}

/** Коды, для которых у кабинета свой текст (с подсказкой, что делать). */
const SITE_VIEW_ERROR_TEXT: Readonly<Record<string, string>> = {
  site_view_forbidden:
    "Вид сайта меняют владелец магазина и сотрудники с правом настроек или контента.",
  site_view_nothing_to_publish: "Нечего публиковать — черновик совпадает с сайтом.",
  site_view_draft_changed: "Черновик изменился, пока вы смотрели. Проверьте изменения ещё раз.",
  site_view_publish_conflict: "Вид сайта только что опубликовал другой сотрудник. Обновите страницу.",
  site_view_media_type_invalid: "Подходят PNG или WebP. SVG пока не принимаем.",
  // Р3г (db-design-r3g §4): 422 части COUNTERS — поля кабинета проверяют то же до запроса.
  site_counters_notice_forbidden: "Платформа запретила режим «Уведомление» — выберите «Только после согласия», иначе черновик не сохранится.",
  site_counters_notice_ack_required: "Режим «Уведомление» не сохранился: нужна галочка в окне о риске. Выберите «Только после согласия», затем снова «Уведомление».",
  site_counters_metrika_id_invalid: "Номер счётчика Метрики — только цифры, без нуля в начале.",
  site_counters_vk_id_invalid: "Номер пикселя VK Рекламы — только цифры, без нуля в начале.",
  site_counters_intro_too_long: `Вступление баннера — не длиннее ${SITE_LIMITS.consentIntro} знаков.`,
  site_counters_text_version_unknown: "Такой редакции текстов баннера на платформе нет. Обновите тексты до текущей редакции.",
  site_counters_intro_markup: "Вступление баннера — простой текст, без ссылок и угловых скобок.",
  site_preview_not_configured: "Предпросмотр сейчас недоступен. Настройки можно менять — черновик сохраняется.",
  network_error: "Нет связи с сервером. Проверьте интернет и повторите.",
};

/** Текст ошибки для уведомления: свой из реестра, иначе — сообщение сервера. */
export function siteViewErrorText(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return SITE_VIEW_ERROR_TEXT[error.code] ?? (error.message || fallback);
  }
  return fallback;
}

/** Код ошибки (или `null`, если это не ошибка API). */
export function apiErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/** Первая ошибка поля из `details.issues` (400 `site_theme_invalid`, `site_contacts_invalid`). */
export function firstIssueMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || !isObject(error.details)) return null;
  const issues = error.details.issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const first: unknown = issues[0];
  return isObject(first) && typeof first.message === "string" ? first.message : null;
}
